/* ==========================================================================
   KEIS — core behaviour. Custom elements, no framework, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents) {
    var fmt = (window.KEIS && window.KEIS.moneyFormat) || '£{{amount}}';
    var withComma = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  /* Strings come from window.KEIS.strings, filled by Liquid in theme.liquid and
     therefore already in the customer's language. The second argument is the
     English wording, used only if that block failed to render — never as the
     normal path. */
  function keisText(key, fallback) {
    var bag = (window.KEIS && window.KEIS.strings) || {};
    return bag[key] != null ? bag[key] : fallback;
  }
  window.KEIS = window.KEIS || {};
  window.KEIS.text = keisText;

  /* Grouping separators differ by language, so the number follows the locale
     rather than a hard-coded en-GB. */
  function keisLocale() {
    return (window.KEIS && window.KEIS.locale) ||
           document.documentElement.lang || 'en';
  }

    var noDecimals = Math.round(cents / 100).toLocaleString(keisLocale());
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1'));
  };

  var routes = function () {
    return (window.KEIS && window.KEIS.routes) || {};
  };

  /* ---------------------------------------------------------------- events */

  var bus = {
    on: function (name, fn) { document.addEventListener('keis:' + name, fn); },
    emit: function (name, detail) {
      document.dispatchEvent(new CustomEvent('keis:' + name, { detail: detail || {}, bubbles: true }));
    }
  };
  window.KEIS = window.KEIS || {};
  window.KEIS.bus = bus;
  window.KEIS.money = money;

  /* --------------------------------------------------------- focus trap */

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, opener) {
    var previous = opener || document.activeElement;
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.filter.call(
        container.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    var target = container.querySelector('[autofocus]') || container.querySelector(FOCUSABLE);
    var released = false;
    /* Tried now, and again once the panel is showing. A modal fades in with a
       visibility transition, and at the instant it opens it is still hidden —
       a hidden element refuses focus without an error, so a single attempt
       here silently left focus behind on the button that opened it, in every
       modal on the site. Keyboard users then tabbed around the page BEHIND
       the dialog, and the Tab trap, listening on the dialog, never fired.
       The timeout is the fallback for a background tab, where animation
       frames do not run. */
    function place() {
      if (released || !target || container.contains(document.activeElement)) return;
      target.focus({ preventScroll: true });
    }
    place();
    requestAnimationFrame(function () { requestAnimationFrame(place); });
    setTimeout(place, 120);
    return function release() {
      released = true;
      container.removeEventListener('keydown', onKey);
      if (previous && previous.focus) previous.focus();
    };
  }

  /* ------------------------------------------------------ scroll locking */

  var lockCount = 0, savedTop = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    savedTop = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -savedTop + 'px';
    document.body.style.width = '100%';
    document.documentElement.classList.add('keis-locked');
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.classList.remove('keis-locked');
    window.scrollTo(0, savedTop);
  }
  /* A page restored from the back/forward cache keeps whatever lock was on
     when it was left (a menu link followed from the open drawer, say). If no
     drawer is open any more, the page must not stay pinned. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted || document.querySelector('keis-drawer[open]')) return;
    if (document.body.style.position !== 'fixed') return;
    lockCount = 1;
    unlockScroll();
  });

  /* ============================================================ <keis-drawer>
     Generic drawer / sheet / modal controller.
     Open with: <button data-keis-open="cart-drawer">
     ========================================================================= */

  var KeisDrawer = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.setPrototypeOf(El, HTMLElement);

    El.prototype.connectedCallback = function () {
      var self = this;
      this.panel = this.querySelector('[data-panel]');
      this.overlayEl = this.querySelector('[data-overlay]');
      this._release = null;

      this.addEventListener('click', function (e) {
        var hit = e.target.closest('[data-keis-close]') || e.target === self.overlayEl;

        /* Clicking the dark surround of a modal has to close it too, and the
           overlay test above never catches that: .keis-overlay sits at z-index
           60 while .keis-modal covers the whole viewport at 70, so the click
           lands on the modal wrapper and the overlay is never the target. The
           wrapper centres its panel, which means a click whose target IS the
           wrapper is by definition a click on the surround. Side drawers keep
           the old behaviour — their panel is the drawer itself, and clicking
           its own padding should not dismiss it. */
        if (!hit && self.panel && e.target === self.panel &&
            self.panel.classList.contains('keis-modal')) {
          hit = true;
        }

        if (hit) {
          e.preventDefault();
          self.close();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.hasAttribute('open')) self.close();
      });
    };

    El.prototype.open = function (opener) {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      if (this.overlayEl) this.overlayEl.classList.add('is-open');
      if (this.panel) this.panel.classList.add('is-open');
      lockScroll();
      this._release = trapFocus(this.panel || this, opener);
      bus.emit('drawer:open', { id: this.id });
    };

    El.prototype.close = function () {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      if (this.overlayEl) this.overlayEl.classList.remove('is-open');
      if (this.panel) this.panel.classList.remove('is-open');
      unlockScroll();
      if (this._release) { this._release(); this._release = null; }
      bus.emit('drawer:close', { id: this.id });
    };

    return El;
  })();
  if (!customElements.get('keis-drawer')) customElements.define('keis-drawer', KeisDrawer);

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-keis-open]');
    if (!opener) return;
    var target = document.getElementById(opener.getAttribute('data-keis-open'));
    if (!target || !target.open) return;
    e.preventDefault();
    target.open(opener);
  });

  /* ============================================================ Scroll-to
     A link or button with data-keis-scroll="name" scrolls to the section
     whose Anchor is that name — the help tiles and their pop-up buttons use it
     to land on the contact form.

     Not a plain #link jump, for two reasons. The header is sticky, so a native
     jump puts the section's heading underneath it. And a pop-up button has to
     close its pop-up first: closing unlocks the page scroll (restoring where
     the page was) and hands focus back to the tile, and either would undo a
     scroll made before it. So the scroll waits for both, then moves.
     ========================================================================= */

  function scrollToAnchor(name) {
    var target = document.getElementById(name);
    if (!target) return false;
    var header = document.querySelector('.keis-header');
    var offset = header ? header.getBoundingClientRect().height : 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 12;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    /* Keyboard and screen-reader users land where they were sent, rather than
       being left on the tile they pressed. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    return true;
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-keis-scroll]');
    if (!link) return;
    var name = link.getAttribute('data-keis-scroll');
    if (!document.getElementById(name)) return;   // no such section: let the link do what links do
    e.preventDefault();
    var drawer = link.closest('keis-drawer');
    if (drawer && drawer.hasAttribute('open')) {
      drawer.close();
      setTimeout(function () { scrollToAnchor(name); }, 30);
    } else {
      scrollToAnchor(name);
    }
  });

  /* A help pop-up's video is only fetched when the pop-up opens, and stopped
     when it closes — otherwise a closed pop-up keeps playing sound, and a
     page of six tiles loads six players up front. */
  bus.on('drawer:open', function (e) {
    /* The bus hands over the event; what was emitted is its detail. */
    var d = e && e.detail;
    var el = d && document.getElementById(d.id);
    if (!el) return;
    Array.prototype.forEach.call(el.querySelectorAll('iframe[data-src]'), function (f) {
      if (!f.getAttribute('src')) f.setAttribute('src', f.getAttribute('data-src'));
    });
  });
  bus.on('drawer:close', function (e) {
    /* The bus hands over the event; what was emitted is its detail. */
    var d = e && e.detail;
    var el = d && document.getElementById(d.id);
    if (!el) return;
    Array.prototype.forEach.call(el.querySelectorAll('iframe[data-src]'), function (f) {
      f.removeAttribute('src');
    });
  });

  /* In the theme editor, selecting a help tile opens its pop-up, so what is
     being edited is on screen — and deselecting closes it again. */
  document.addEventListener('shopify:block:select', function (e) {
    var id = e.detail && e.detail.blockId;
    var modal = id && document.querySelector('keis-drawer[data-help-block="' + id + '"]');
    if (modal && modal.open) modal.open();
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var id = e.detail && e.detail.blockId;
    var modal = id && document.querySelector('keis-drawer[data-help-block="' + id + '"]');
    if (modal && modal.close) modal.close();
  });

  /* ============================================================ Recently viewed
     Kept in the customer's own browser under one key: a list of product
     handles, newest first. Nothing about it is sent anywhere — it exists only
     so the Recommended products section can show someone what they were
     looking at. Storage can be switched off or full, so every read and write
     is allowed to fail quietly and the section falls back to best sellers.
     ========================================================================= */

  var RECENT_KEY = 'keis:recent';

  function readRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list)
        ? list.filter(function (h) { return typeof h === 'string' && h; })
        : [];
    } catch (e) { return []; }
  }

  function recordView() {
    var mark = document.querySelector('[data-keis-viewed]');
    if (!mark) return;
    var handle = mark.getAttribute('data-keis-viewed');
    if (!handle) return;
    var keep = Number(mark.getAttribute('data-keep')) || 12;
    var list = readRecent().filter(function (h) { return h !== handle; });
    list.unshift(handle);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, keep))); } catch (e) {}
  }

  function rootUrl(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  function cardsFrom(html) {
    var box = document.createElement('div');
    box.innerHTML = html;
    return Array.prototype.filter.call(box.children, function (el) { return el.nodeType === 1; });
  }

  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    var m = a && a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* Swaps the heading to match what is actually shown. */
  function setRecsHeading(root, eyebrow, title) {
    var head = root.querySelector('[data-keis-recs-heading]');
    if (!head) return;
    var eb = head.querySelector('.keis-eyebrow');
    var h = head.querySelector('.keis-section-heading__title');
    if (h) h.textContent = title || '';
    if (eb) {
      if (eyebrow) eb.textContent = eyebrow;
      else eb.remove();
    } else if (eyebrow && h) {
      var span = document.createElement('span');
      span.className = 'keis-eyebrow';
      span.textContent = eyebrow;
      h.parentNode.insertBefore(span, h);
    }
  }

  function initRecs(root) {
    if (root.__keisRecs) return;
    root.__keisRecs = true;
    var grid = root.querySelector('[data-keis-recs-grid]');
    if (!grid) return;
    var limit = Number(root.getAttribute('data-limit')) || 4;
    var exclude = (root.getAttribute('data-exclude') || '').split(',').filter(Boolean);
    var wanted = function (h) { return h && exclude.indexOf(h) === -1; };

    /* With nothing at all to show, the section goes rather than leaving a
       heading over an empty row. */
    function settle() {
      var section = root.closest('.keis-section');
      if (section) section.hidden = !grid.children.length;
    }

    /* The row is made up in order: the product's picks (rendered by Liquid,
       never replaced), then this customer's recently viewed, then best
       sellers, until it holds `limit` cards. Liquid has already put the chosen
       best sellers after the picks, so the page is full without JS; here they
       give way to recently viewed ones. */
    var nPicks = Number(root.getAttribute('data-picks')) || 0;
    var kids = Array.prototype.slice.call(grid.children);
    var picks = kids.slice(0, nPicks);
    var fillers = kids.slice(nPicks);
    var taken = picks.map(handleOf).filter(Boolean);
    var free = function (h) { return wanted(h) && taken.indexOf(h) === -1; };
    var take = function (cards, room) {
      var out = [];
      cards.forEach(function (c) {
        var h = handleOf(c);
        if (out.length < room && h && free(h)) { out.push(c); taken.push(h); }
      });
      return out;
    };

    if (picks.length >= limit) { settle(); return; }

    function finish(recentCards) {
      var row = picks.concat(recentCards);
      row = row.concat(take(fillers, limit - row.length));
      var done = function () {
        grid.replaceChildren.apply(grid, row);
        if (!picks.length && recentCards.length) {
          setRecsHeading(root, root.getAttribute('data-recent-eyebrow'), root.getAttribute('data-recent-title'));
        }
        settle();
      };
      var url = root.getAttribute('data-best-url');
      if (row.length >= limit || !url) { done(); return; }
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          row = row.concat(take(cardsFrom(html), limit - row.length));
          done();
        })
        .catch(done);
    }

    var recent = root.getAttribute('data-recent') === 'false'
      ? []
      : readRecent().filter(free).slice(0, limit - picks.length);
    if (!recent.length) { finish([]); return; }

    /* One card each, rendered by the theme's own card snippet, in the order
       they were viewed. A product since deleted or unpublished simply does not
       come back and is skipped. */
    Promise.all(recent.map(function (h) {
      return fetch(rootUrl('/products/' + encodeURIComponent(h) + '?view=keis-card'), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (pages) {
      var cards = pages.map(function (html) { return cardsFrom(html)[0]; }).filter(Boolean);
      finish(take(cards, limit - picks.length));
    });
  }

  function initAllRecs() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-keis-recs]'), initRecs);
  }

  /* Recommendations are worked out BEFORE this view is recorded, so a product
     page never recommends itself — the exclude list would catch it anyway,
     but there is no reason to rely on that. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllRecs(); recordView(); });
  } else {
    initAllRecs();
    recordView();
  }
  document.addEventListener('shopify:section:load', initAllRecs);

  /* ====================================================== <keis-announcement>
     Rotates messages on a timer with manual advance. Stacked slides, so the
     bar never changes height.
     ========================================================================= */

  var KeisAnnouncement = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.setPrototypeOf(El, HTMLElement);

    El.prototype.connectedCallback = function () {
      var self = this;
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.keis-announce__slide'));
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;

      this.querySelectorAll('[data-announce-next]').forEach(function (b) {
        b.addEventListener('click', function () { self.go(self.index + 1); self.restart(); });
      });
      this.querySelectorAll('[data-announce-prev]').forEach(function (b) {
        b.addEventListener('click', function () { self.go(self.index - 1); self.restart(); });
      });

      this.addEventListener('mouseenter', function () { self.stop(); });
      this.addEventListener('mouseleave', function () { self.start(); });
      this.start();
    };

    El.prototype.go = function (i) {
      var n = this.slides.length;
      this.index = ((i % n) + n) % n;
      for (var j = 0; j < n; j++) {
        this.slides[j].classList.toggle('is-active', j === this.index);
        this.slides[j].setAttribute('aria-hidden', j === this.index ? 'false' : 'true');
      }
    };
    El.prototype.start = function () {
      var self = this;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(function () { self.go(self.index + 1); }, this.interval);
    };
    El.prototype.stop = function () { clearInterval(this.timer); };
    El.prototype.restart = function () { this.stop(); this.start(); };
    El.prototype.disconnectedCallback = function () { this.stop(); };

    return El;
  })();
  if (!customElements.get('keis-announcement')) customElements.define('keis-announcement', KeisAnnouncement);

  /* =============================================================== cart API */

  var Cart = {
    add: function (formDataOrItems) {
      var body, headers = { 'Accept': 'application/json' };
      if (formDataOrItems instanceof FormData) {
        body = formDataOrItems;
      } else {
        body = JSON.stringify(formDataOrItems);
        headers['Content-Type'] = 'application/json';
      }
      return fetch((routes().cart_add_url || '/cart/add') + '.js', {
        method: 'POST', headers: headers, body: body
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },
    change: function (payload) {
      return fetch((routes().cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) { return r.json(); });
    },
    get: function () {
      return fetch((routes().cart_url || '/cart') + '.js', {
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); });
    },
    /* Re-render every cart-bound region through the Section Rendering API.
       Regions opt in with data-cart-section="<section id>". */
    refresh: function () {
      var targets = Array.prototype.slice.call(document.querySelectorAll('[data-cart-section]'));
      if (!targets.length) return Cart.get().then(updateCartCount);

      var ids = targets.map(function (el) { return el.dataset.cartSection; });
      var unique = ids.filter(function (id, i) { return id && ids.indexOf(id) === i; });

      return fetch(window.location.pathname + '?sections=' + encodeURIComponent(unique.join(',')))
        .then(function (r) { return r.json(); })
        .then(function (data) {
          targets.forEach(function (el) {
            var html = data[el.dataset.cartSection];
            if (!html) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-cart-section="' + el.dataset.cartSection + '"]');
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
          return Cart.get();
        })
        .then(function (cart) { updateCartCount(cart); return cart; });
    }
  };
  window.KEIS.cart = Cart;

  function updateCartCount(cart) {
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-total]').forEach(function (el) {
      el.innerHTML = money(cart.total_price);
    });
    /* Free-delivery progress, keyed to the threshold in theme settings. */
    var threshold = (window.KEIS && window.KEIS.freeShippingThreshold) || 0;
    if (threshold > 0) {
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      document.querySelectorAll('[data-ship-fill]').forEach(function (el) { el.style.width = pct + '%'; });
      var remaining = threshold - cart.total_price;
      var qualified = remaining <= 0;
      document.querySelectorAll('[data-ship-bar]').forEach(function (el) {
        el.classList.toggle('is-qualified', qualified);
      });
      document.querySelectorAll('[data-ship-label]').forEach(function (el) {
        var target = el.querySelector('[data-ship-text]') || el;
        target.innerHTML = qualified
          ? (el.dataset.done || keisText('shippingEarned', 'You have earned FREE UK delivery'))
          : (el.dataset.away || keisText('shippingAway', 'Spend {amount} more for free UK delivery'))
              .replace('{amount}', money(remaining));
      });
    }
    bus.emit('cart:updated', { cart: cart });
  }
  window.KEIS.updateCartCount = updateCartCount;

  /* ========================================================= <keis-add-form>
     Progressive enhancement over the standard product form: posts via AJAX,
     opens the cart drawer, falls back to a normal submit without JS.
     ========================================================================= */

  var KeisAddForm = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.setPrototypeOf(El, HTMLElement);

    El.prototype.connectedCallback = function () {
      var self = this;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');

      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit();
      });
    };

    El.prototype.submit = function () {
      var self = this;
      var label = this.button ? this.button.innerHTML : '';
      if (this.button) { this.button.setAttribute('aria-disabled', 'true'); this.button.innerHTML = this.dataset.adding || keisText('adding', 'Adding…'); }
      if (this.errorEl) this.errorEl.hidden = true;

      var data = new FormData(this.form);
      /* Bundle partner: a second line item added alongside the garment. */
      var bundle = document.querySelector('[data-bundle-toggle].is-on');
      var items = null;
      if (bundle && bundle.dataset.bundleVariant) {
        items = { items: [
          { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) },
          { id: Number(bundle.dataset.bundleVariant), quantity: 1 }
        ] };
      }

      Cart.add(items || data)
        .then(function () { return Cart.refresh(); })
        .then(function () {
          var drawer = document.getElementById('keis-cart-drawer');
          if (drawer && drawer.open && self.dataset.openDrawer !== 'false') drawer.open(self.button);
          bus.emit('cart:added', {});
        })
        .catch(function (err) {
          if (self.errorEl) {
            self.errorEl.textContent = (err && (err.description || err.message)) || keisText('cartError', 'Could not add to cart.');
            self.errorEl.hidden = false;
          }
        })
        .finally(function () {
          if (self.button) { self.button.removeAttribute('aria-disabled'); self.button.innerHTML = label; }
        });
    };

    return El;
  })();
  if (!customElements.get('keis-add-form')) customElements.define('keis-add-form', KeisAddForm);

  /* ============================================================ quantity UI */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    var wrap = btn.closest('.keis-qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var step = parseInt(btn.dataset.qtyStep, 10);
    var min = parseInt(input.min, 10) || 1;
    var next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    if (input.max) next = Math.min(parseInt(input.max, 10), next);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Cart line quantity + removal */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    Cart.change({ line: Number(input.dataset.line), quantity: Number(input.value) })
      .then(function () { return Cart.refresh(); });
  });
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    Cart.change({ line: Number(rm.dataset.line), quantity: 0 }).then(function () { return Cart.refresh(); });
  });

  /* ====================================================== <keis-sticky-bar>
     Shows once the main add-to-cart button scrolls out of view.
     ========================================================================= */

  var KeisStickyBar = (function () {
    function El() { return Reflect.construct(HTMLElement, [], El); }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.setPrototypeOf(El, HTMLElement);

    El.prototype.connectedCallback = function () {
      var self = this;
      var watch = document.querySelector(this.dataset.watch || '[data-buy-buttons]');

      var dismiss = this.querySelector('[data-sticky-dismiss]');
      if (dismiss) {
        dismiss.addEventListener('click', function () {
          self.classList.add('is-dismissed');
          document.documentElement.classList.remove('keis-sticky-on');
          /* Stays dismissed for this product until the tab is closed. */
          try { sessionStorage.setItem('keis:sticky-dismissed:' + window.location.pathname, '1'); } catch (e) {}
        });
        try {
          if (sessionStorage.getItem('keis:sticky-dismissed:' + window.location.pathname)) {
            this.classList.add('is-dismissed');
          }
        } catch (e) {}
      }

      if (!watch || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(function (entries) {
        var on = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        self.classList.toggle('is-visible', on);
        /* Floating chat bubbles (Gorgias) sit in the bottom corner, exactly
           over the bar's add-to-cart button, and took the tap. They are
           lifted clear of the bar while it shows. */
        var shown = on && !self.classList.contains('is-dismissed');
        document.documentElement.classList.toggle('keis-sticky-on', shown);
        if (shown) document.documentElement.style.setProperty('--keis-sticky-h', self.offsetHeight + 'px');
      }, { rootMargin: '0px 0px 0px 0px' });
      this.observer.observe(watch);
    };
    El.prototype.disconnectedCallback = function () { if (this.observer) this.observer.disconnect(); };
    return El;
  })();
  if (!customElements.get('keis-sticky-bar')) customElements.define('keis-sticky-bar', KeisStickyBar);

  /* ============================================================== reveal */

  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var revealObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    /* Anything already on screen when the page arrives is shown at once and
       WITHOUT a transition. It used to be revealed by the observer like
       everything else, so the top of the page loaded blank and then faded in
       section by section — the page appearing to arrive twice. The animation is
       for content scrolled TO, which is the only place it means anything. */
    var showNow = function (el) {
      el.classList.add('is-revealed');
      if (revealObserver) revealObserver.unobserve(el);
    };
    var inFirstView = function (el) {
      var r = el.getBoundingClientRect();
      return r.top < (window.innerHeight || 0) && r.bottom > 0;
    };

    var observeReveals = function (root) {
      (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)').forEach(function (el) {
        if (!document.documentElement.classList.contains('keis-reveal-ready') && inFirstView(el)) {
          showNow(el);
          return;
        }
        revealObserver.observe(el);
      });
    };

    /* The transition is suppressed until the first frame has been painted, so
       the elements shown above cannot animate on their way in. Two frames:
       one for the class to land, one for it to have been painted with. */
    var markReady = function () {
      var done = false;
      var ready = function () {
        if (done) return;
        done = true;
        document.documentElement.classList.add('keis-reveal-ready');
      };
      /* Two frames, so the class lands after the first paint. A BACKGROUND tab
         paints no frames at all, though — a link opened in one would never
         reach this and would lose its transitions for the whole visit — so a
         timer backs it up. */
      requestAnimationFrame(function () { requestAnimationFrame(ready); });
      setTimeout(ready, 400);
    };

    observeReveals();
    document.addEventListener('DOMContentLoaded', function () { observeReveals(); });
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      markReady();
    } else {
      window.addEventListener('load', markReady);
      document.addEventListener('DOMContentLoaded', markReady);
    }
    document.addEventListener('shopify:section:load', function (e) { observeReveals(e.target); });
  } else {
    document.querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('is-revealed'); });
  }

  /* ============================================== header stuck state + offset */

  var header = document.querySelector('.keis-header');
  if (header) {
    var setOffset = function () {
      document.documentElement.style.setProperty('--sticky-offset', header.offsetHeight + 'px');
    };
    setOffset();
    window.addEventListener('resize', setOffset);
    window.addEventListener('scroll', function () {
      header.classList.toggle('is-stuck', window.scrollY > 8);
    }, { passive: true });
  }

  /* ================================================================ tooltips */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-tip-toggle]');
    document.querySelectorAll('[data-tip-bubble]').forEach(function (b) {
      var own = trigger && trigger.parentElement.contains(b);
      b.hidden = !own || !b.hidden ? !own : false;
    });
  });
  document.querySelectorAll('.keis-tip').forEach(function (tip) {
    var bubble = tip.querySelector('[data-tip-bubble]');
    if (!bubble) return;
    tip.addEventListener('mouseenter', function () { bubble.hidden = false; });
    tip.addEventListener('mouseleave', function () { bubble.hidden = true; });
    tip.addEventListener('focusin', function () { bubble.hidden = false; });
    tip.addEventListener('focusout', function () { bubble.hidden = true; });
  });

  /* Boot the cart count from the rendered cart on first paint. */
  document.addEventListener('DOMContentLoaded', function () {
    if (document.querySelector('[data-ship-fill]')) Cart.get().then(updateCartCount);
  });
})();

/* ======================================================== <keis-hero-video>
   The poster image is already painted by the time this runs. The video is
   attached on idle so it never competes with the LCP text.
   ========================================================================= */
(function () {
  'use strict';
  function El() { return Reflect.construct(HTMLElement, [], El); }
  El.prototype = Object.create(HTMLElement.prototype);
  El.prototype.constructor = El;
  Object.setPrototypeOf(El, HTMLElement);

  /* Who gets the video at all. Not somebody who asked for less motion, not a
     phone unless the section says so (it is megabytes of their data for a
     background), and not a connection that is already struggling. */
  El.prototype.wanted = function () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return false;
    var c = navigator.connection;
    if (c && (c.saveData || /^(slow-)?2g$/.test(c.effectiveType || ''))) return false;
    return true;
  };

  El.prototype.connectedCallback = function () {
    var self = this;
    if (!this.wanted()) return;
    var start = function () { self.attach(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2500 });
    else window.setTimeout(start, 1200);
  };

  El.prototype.attach = function () {
    /* Shopify's own <video> markup, kept inert in a <template> until now. */
    var tpl = this.querySelector('template');
    if (tpl) {
      this.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
    }
    Array.prototype.forEach.call(this.querySelectorAll('img:not([alt])'), function (i) { i.alt = ''; });
    var existing = this.querySelector('video');
    if (existing) {
      existing.setAttribute('preload', 'auto');
      existing.muted = true;
      var p = existing.play();
      if (p && p.catch) p.catch(function () {});
      return;
    }
    var src = this.dataset.src;
    if (!src) return;
    var v = document.createElement('video');
    v.src = src;
    v.autoplay = true; v.loop = true; v.muted = true;
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.tabIndex = -1;
    this.appendChild(v);
  };

  if (!customElements.get('keis-hero-video')) customElements.define('keis-hero-video', El);
})();

/* ======================================================== <keis-lazy-video>
   A video that is not fetched until it is nearly on screen.

   Background clips in a grid or a feature row are decoration, and an
   autoplaying <video> starts downloading the moment it is parsed whatever
   `preload` says — one collection video was 8.5MB, which on a phone connection
   pushed the page's main image back by eight seconds. The markup is held in a
   <template> (inert: nothing inside one is fetched) and cloned in when the
   tile comes within a screen of the viewport.

   Skipped entirely for a visitor who asked for less motion or is on a metered
   or slow connection — the poster image already says what the clip says.
   ========================================================================= */
(function () {
  'use strict';
  function El() { return Reflect.construct(HTMLElement, [], El); }
  El.prototype = Object.create(HTMLElement.prototype);
  El.prototype.constructor = El;
  Object.setPrototypeOf(El, HTMLElement);

  El.prototype.connectedCallback = function () {
    var self = this;
    var c = navigator.connection;
    if (c && (c.saveData || /^(slow-)?2g$/.test(c.effectiveType || ''))) return;
    if (this.dataset.autoplay === 'true' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!('IntersectionObserver' in window)) { this.attach(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.disconnect();
        self.attach();
      });
    }, { rootMargin: '100% 0px' });
    io.observe(this);
  };

  El.prototype.attach = function () {
    var tpl = this.querySelector('template');
    if (!tpl) return;
    this.appendChild(tpl.content.cloneNode(true));
    tpl.remove();
    /* Shopify's video_tag includes an <img> fallback with no alt of its own.
       It is decoration beside the clip, so it is marked as such. */
    Array.prototype.forEach.call(this.querySelectorAll('img:not([alt])'), function (i) { i.alt = ''; });
    var v = this.querySelector('video');
    if (v && v.autoplay) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
  };

  if (!customElements.get('keis-lazy-video')) customElements.define('keis-lazy-video', El);
})();

/* ========================================================= <keis-load-more>
   Replaces pagination. Appends the next page in place, preserves scroll, and
   keeps the "showing X of Y" counter and progress bar in step.
   ========================================================================= */
(function () {
  'use strict';
  function El() { return Reflect.construct(HTMLElement, [], El); }
  El.prototype = Object.create(HTMLElement.prototype);
  El.prototype.constructor = El;
  Object.setPrototypeOf(El, HTMLElement);

  El.prototype.connectedCallback = function () {
    var self = this;
    this.button = this.querySelector('[data-loadmore-button]');
    this.grid = document.querySelector(this.dataset.target);
    if (!this.button || !this.grid) return;
    this.button.addEventListener('click', function () { self.load(); });
  };

  El.prototype.load = function () {
    var self = this;
    var next = this.dataset.next;
    if (!next || this.busy) return;
    this.busy = true;
    var label = this.button.innerHTML;
    this.button.setAttribute('aria-disabled', 'true');
    this.button.innerHTML = this.button.dataset.loading || keisText('loading', 'Loading…');

    fetch(next)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshGrid = doc.querySelector(self.dataset.target);
        if (freshGrid) {
          var added = Array.prototype.slice.call(freshGrid.children);
          added.forEach(function (node) { self.grid.appendChild(node); });
        }

        /* Carry the next link forward, or retire the button. */
        var freshLoader = doc.querySelector('keis-load-more');
        var nextUrl = freshLoader && freshLoader.dataset.next;
        if (nextUrl) {
          self.dataset.next = nextUrl;
        } else {
          delete self.dataset.next;
          self.button.remove();
        }

        /* Counter + progress. */
        var meta = self.querySelector('[data-loadmore-meta]');
        var freshMeta = freshLoader && freshLoader.querySelector('[data-loadmore-meta]');
        if (meta && freshMeta) meta.textContent = freshMeta.textContent;

        var fill = self.querySelector('[data-loadmore-fill]');
        var freshFill = freshLoader && freshLoader.querySelector('[data-loadmore-fill]');
        if (fill && freshFill) fill.style.width = freshFill.style.width;

        /* Update the address bar so a refresh keeps the reader's place. */
        window.history.replaceState({}, '', next);

        document.dispatchEvent(new CustomEvent('keis:grid:appended', { bubbles: true }));
      })
      .catch(function () { /* leave the button in place to retry */ })
      .finally(function () {
        self.busy = false;
        if (self.button && self.button.isConnected) {
          self.button.removeAttribute('aria-disabled');
          self.button.innerHTML = label;
        }
      });
  };

  if (!customElements.get('keis-load-more')) customElements.define('keis-load-more', El);
})();

/* ============================================================== <keis-tabs> */
(function () {
  'use strict';
  function El() { return Reflect.construct(HTMLElement, [], El); }
  El.prototype = Object.create(HTMLElement.prototype);
  El.prototype.constructor = El;
  Object.setPrototypeOf(El, HTMLElement);

  El.prototype.connectedCallback = function () {
    var self = this;
    this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tab]'));
    this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-panel]'));
    if (!this.tabs.length) return;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { self.select(tab.dataset.tab); });
    });

    /* Deep link: #warranty opens that tab. */
    var hash = window.location.hash.replace('#', '');
    if (hash && this.tabs.some(function (t) { return t.dataset.tab === hash; })) this.select(hash);
  };

  El.prototype.select = function (name) {
    this.tabs.forEach(function (t) {
      t.setAttribute('aria-selected', String(t.dataset.tab === name));
    });
    this.panels.forEach(function (p) {
      p.hidden = p.dataset.panel !== name;
    });
  };

  if (!customElements.get('keis-tabs')) customElements.define('keis-tabs', El);
})();

/* ==================================================== article read progress */
(function () {
  'use strict';
  var fill = document.querySelector('[data-read-progress]');
  var body = document.querySelector('[data-article-body]');
  if (!fill || !body) return;

  var update = function () {
    var rect = body.getBoundingClientRect();
    var total = rect.height - window.innerHeight;
    if (total <= 0) { fill.style.width = '100%'; return; }
    var done = Math.min(1, Math.max(0, -rect.top / total));
    fill.style.width = (done * 100).toFixed(1) + '%';
  };
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
})();

/* ================================= product recommendations (section render) */
(function () {
  'use strict';
  var host = document.getElementById('keis-recommendations');
  if (!host || !host.dataset.url || host.children.length) return;
  fetch(host.dataset.url)
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.getElementById('keis-recommendations');
      if (fresh) host.innerHTML = fresh.innerHTML;
    })
    .catch(function () {});
})();

/* ===================================================== logo tile background
   Awards and press badges usually sit on a flat colour. Sizing the logo by
   height inside a 16:9 tile leaves spare width either side, which looks like a
   cut-out floating on the page unless the tile matches the badge.

   The four corners are sampled and the colour applied only when they agree —
   a photographic or transparent logo has no single background colour, so it is
   left on the theme surface instead of being painted a wrong one.
   ========================================================================= */
(function () {
  'use strict';

  var TOLERANCE = 12;   // per-channel spread allowed across the four corners

  function sample(img, tile) {
    var w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    try { ctx.drawImage(img, 0, 0); } catch (e) { return; }

    var pts;
    try {
      pts = [
        ctx.getImageData(1, 1, 1, 1).data,
        ctx.getImageData(w - 2, 1, 1, 1).data,
        ctx.getImageData(1, h - 2, 1, 1).data,
        ctx.getImageData(w - 2, h - 2, 1, 1).data
      ];
    } catch (e) {
      return;                       /* canvas tainted — leave the tile alone */
    }

    /* A transparent corner means a cut-out logo: no background to match. */
    for (var i = 0; i < pts.length; i++) if (pts[i][3] < 250) return;

    for (var ch = 0; ch < 3; ch++) {
      var vals = pts.map(function (p) { return p[ch]; });
      if (Math.max.apply(null, vals) - Math.min.apply(null, vals) > TOLERANCE) return;
    }

    var r = Math.round((pts[0][0] + pts[1][0] + pts[2][0] + pts[3][0]) / 4);
    var g = Math.round((pts[0][1] + pts[1][1] + pts[2][1] + pts[3][1]) / 4);
    var b = Math.round((pts[0][2] + pts[1][2] + pts[2][2] + pts[3][2]) / 4);
    tile.style.background = 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  function run() {
    document.querySelectorAll('[data-logo-autobg]').forEach(function (tile) {
      var img = tile.querySelector('img');
      if (!img) return;
      if (img.complete) sample(img, tile);
      else img.addEventListener('load', function () { sample(img, tile); }, { once: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
